import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Layout } from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ImageUpload } from "@/components/case-article/editor/ImageUpload";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { InlineEditor } from "@/components/case-article/editor/InlineEditor";
import { toast } from "@/hooks/use-toast";
import { ArrowLeft, Save, Settings, X } from "lucide-react";
import { Link } from "react-router-dom";
import { Json } from "@/integrations/supabase/types";
import { useEditableCaseCategories } from "@/hooks/useEditableCaseCategories";

interface EditorBlock {
  id: string;
  block_type: string;
  content: Record<string, any>;
  sort_order: number;
}

function slugify(text: string) {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .trim();
}

export default function AdminCaseArticleEditor() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { categories: caseCategories } = useEditableCaseCategories();

  const [projectName, setProjectName] = useState("");
  const [slug, setSlug] = useState("");
  const [systemName, setSystemName] = useState("");
  const [clientName, setClientName] = useState("");
  const [heroImageUrl, setHeroImageUrl] = useState("");
  const [category, setCategory] = useState("uncategorized");
  const [tagLight, setTagLight] = useState("");
  const [tagSpatial, setTagSpatial] = useState("");
  const [tagMaterial, setTagMaterial] = useState("");
  const [published, setPublished] = useState(false);
  const [blocks, setBlocks] = useState<EditorBlock[]>([]);
  const [slugManual, setSlugManual] = useState(false);
  const [defaultsApplied, setDefaultsApplied] = useState(false);
  const [showMeta, setShowMeta] = useState(false);

  const { data: article } = useQuery({
    queryKey: ["admin-case-article", id],
    enabled: !!id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("case_articles")
        .select("*")
        .eq("id", id!)
        .single();
      if (error) throw error;
      return data;
    },
  });

  const { data: existingBlocks } = useQuery({
    queryKey: ["admin-case-article-blocks", id],
    enabled: !!id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("case_article_blocks")
        .select("*")
        .eq("article_id", id!)
        .order("sort_order", { ascending: true });
      if (error) throw error;
      return data;
    },
  });

  useEffect(() => {
    if (article) {
      setProjectName(article.project_name);
      setSlug(article.slug);
      setSystemName(article.system_name || "");
      setClientName(article.client_name || "");
      setHeroImageUrl(article.hero_image_url || "");
      setCategory((article as any).category || "uncategorized");
      setTagLight(article.tag_light_condition || "");
      setTagSpatial(article.tag_spatial_type || "");
      setTagMaterial(article.tag_material_type || "");
      setPublished(article.published);
      setSlugManual(true);
    }
  }, [article]);

  useEffect(() => {
    if (existingBlocks) {
      setBlocks(
        existingBlocks.map((b) => ({
          id: b.id,
          block_type: b.block_type,
          content: (b.content as Record<string, any>) || {},
          sort_order: b.sort_order,
        }))
      );
    }
  }, [existingBlocks]);

  useEffect(() => {
    if (isNew && !defaultsApplied) {
      setBlocks([
        { id: crypto.randomUUID(), block_type: "text_section", content: { title: "Light Condition Analysis", showDivider: true }, sort_order: 0 },
        { id: crypto.randomUUID(), block_type: "two_column", content: { title: "Material Strategy", reversed: false }, sort_order: 1 },
        { id: crypto.randomUUID(), block_type: "image", content: { alignment: "center" }, sort_order: 2 },
        { id: crypto.randomUUID(), block_type: "material_reference", content: { materialIds: [] }, sort_order: 3 },
        { id: crypto.randomUUID(), block_type: "gallery", content: { images: [] }, sort_order: 4 },
        { id: crypto.randomUUID(), block_type: "cta", content: { headline: "Request System Matching", buttonText: "Get in Touch" }, sort_order: 5 },
      ]);
      setDefaultsApplied(true);
    }
  }, [isNew, defaultsApplied]);

  useEffect(() => {
    if (!slugManual && projectName) {
      setSlug(slugify(projectName));
    }
  }, [projectName, slugManual]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      let articleId = id;

      if (isNew) {
        const { data, error } = await supabase
          .from("case_articles")
          .insert({
            project_name: projectName,
            slug,
            system_name: systemName || null,
            client_name: clientName || null,
            hero_image_url: heroImageUrl || null,
            category,
            tag_light_condition: tagLight || null,
            tag_spatial_type: tagSpatial || null,
            tag_material_type: tagMaterial || null,
            published,
          } as any)
          .select("id")
          .single();
        if (error) throw error;
        articleId = data.id;
      } else {
        const { error } = await supabase
          .from("case_articles")
          .update({
            project_name: projectName,
            slug,
            system_name: systemName || null,
            client_name: clientName || null,
            hero_image_url: heroImageUrl || null,
            category,
            tag_light_condition: tagLight || null,
            tag_spatial_type: tagSpatial || null,
            tag_material_type: tagMaterial || null,
            published,
          } as any)
          .eq("id", id!);
        if (error) throw error;
      }

      if (!isNew) {
        await supabase.from("case_article_blocks").delete().eq("article_id", articleId!);
      }

      if (blocks.length > 0) {
        const rows = blocks.map((b, i) => ({
          article_id: articleId!,
          block_type: b.block_type,
          sort_order: i,
          content: b.content as Json,
        }));
        const { error } = await supabase.from("case_article_blocks").insert(rows);
        if (error) throw error;
      }

      return articleId;
    },
    onSuccess: (articleId) => {
      toast({ title: "Saved", description: "Article saved successfully." });
      queryClient.invalidateQueries({ queryKey: ["admin-case-article"] });
      if (isNew) navigate(`/admin/cases/${articleId}/edit`, { replace: true });
    },
    onError: (err: any) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  return (
    <Layout>
      <div className="min-h-screen bg-muted relative">
        {/* Sticky top bar */}
        <div className="sticky top-0 z-40 bg-background/95 backdrop-blur border-b border-border">
          <div className="mx-auto max-w-[1400px] flex items-center gap-3 px-4 py-2">
            <Button asChild variant="ghost" size="icon" className="h-8 w-8">
              <Link to="/admin/cases"><ArrowLeft size={16} /></Link>
            </Button>
            <span className="font-serif text-sm text-foreground truncate flex-1">
              {projectName || "New Article"}
            </span>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-2 mr-2">
                <Switch checked={published} onCheckedChange={setPublished} className="scale-75" />
                <span className="font-sans text-[10px] uppercase tracking-wider text-muted-foreground">
                  {published ? "Published" : "Draft"}
                </span>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5 h-8 font-sans text-[10px] uppercase tracking-wider"
                onClick={() => setShowMeta(true)}
              >
                <Settings size={12} /> Details
              </Button>
              <Button
                onClick={() => saveMutation.mutate()}
                disabled={saveMutation.isPending || !projectName || !slug}
                size="sm"
                className="gap-1.5 h-8 font-sans text-[10px] uppercase tracking-wider"
              >
                <Save size={12} /> {saveMutation.isPending ? "Saving…" : "Save"}
              </Button>
            </div>
          </div>
        </div>

        {/* Full-page inline editor */}
        <InlineEditor
          blocks={blocks}
          onChange={setBlocks}
          article={{
            project_name: projectName,
            system_name: systemName,
            hero_image_url: heroImageUrl,
          }}
        />

        {/* Metadata drawer */}
        {showMeta && (
          <>
            <div className="fixed inset-0 z-40 bg-black/20" onClick={() => setShowMeta(false)} />
            <div className="fixed right-0 top-0 z-50 h-full w-[380px] bg-background border-l border-border shadow-2xl flex flex-col animate-in slide-in-from-right duration-200">
              <div className="flex items-center gap-3 px-5 py-4 border-b border-border">
                <Settings size={16} className="text-muted-foreground" />
                <h3 className="font-sans text-sm font-medium text-foreground flex-1">Article Details</h3>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setShowMeta(false)}>
                  <X size={14} />
                </Button>
              </div>
              <div className="flex-1 overflow-y-auto p-5 space-y-4">
                <div>
                  <Label className="font-sans text-xs">Project Name *</Label>
                  <Input value={projectName} onChange={(e) => setProjectName(e.target.value)} />
                </div>
                <div>
                  <Label className="font-sans text-xs">Slug *</Label>
                  <Input value={slug} onChange={(e) => { setSlug(e.target.value); setSlugManual(true); }} />
                </div>
                <div>
                  <Label className="font-sans text-xs">System Name</Label>
                  <Input value={systemName} onChange={(e) => setSystemName(e.target.value)} />
                </div>
                <div>
                  <Label className="font-sans text-xs">Designer</Label>
                  <Input value={clientName} onChange={(e) => setClientName(e.target.value)} placeholder="e.g. MINZO Studio" />
                </div>
                <div>
                  <Label className="font-sans text-xs">Category</Label>
                  <Select value={category} onValueChange={setCategory}>
                    <SelectTrigger className="mt-1"><SelectValue placeholder="Select category…" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="uncategorized">Uncategorized</SelectItem>
                      {caseCategories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <ImageUpload label="Hero Image" value={heroImageUrl} onChange={setHeroImageUrl} />
              </div>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
